import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  ThemeProvider,
  createTheme
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import loyaltyService from '../../services/loyaltyService';
import { useAuth } from '../../context/AuthContext';

const GroupSignUp = () => {
  const navigate = useNavigate();
  const { org, loading } = useAuth();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Create dynamic theme based on group colors
  const theme = useMemo(() => {
    const defaultTheme = createTheme();
    
    if (!org || typeof org === 'string') return defaultTheme;

    const customTheme = {
      ...defaultTheme,
      palette: {
        ...defaultTheme.palette,
        primary: {
          main: org.primary_color || '#1976d2',
          contrastText: '#ffffff',
        },
        secondary: {
          main: org.secondary_color || '#f50057',
          contrastText: '#ffffff',
        },
        background: {
          default: '#f5f5f5',
        }
      },
    };

    return createTheme(customTheme);
  }, [org]);

  // Helper to get the appropriate logo
  const getLogo = () => {
    if (!org || typeof org === 'string') return null;
    if (org.location_logo) return org.location_logo;
    if (org.company_logo) return org.company_logo;
    return null;
  };

  // Helper to get the appropriate name
  const getName = () => {
    if (!org || typeof org === 'string') return 'our rewards program';
    if (org.location_name) return org.location_name;
    if (org.company_name) return org.company_name;
    return 'our rewards program';
  };


  const validatePhone = () => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (!cleanPhone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (cleanPhone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
      // Clear error when user starts typing
      if (phoneError) {
        setPhoneError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePhone()) {
      return;
    }
    
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      // Make API call to enroll with just phone number
      const cleanPhone = phone.replace(/\D/g, '');
      
      // Use org.id if org is an object, otherwise use org as string
      const groupId = org && typeof org === 'object' ? org.id : org;
      await loyaltyService.publicPhoneEnroll(groupId, cleanPhone);
      
      setSubmitSuccess(true);
      
      // Don't redirect, just show success message
      
    } catch (err) {
      if (err.response && err.response.data) {
        setSubmitError(err.response.data.message || err.response.data.error || err.response.data?.phone?.[0] || 'Enrollment failed');
      } else {
        setSubmitError('Enrollment failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatPhoneDisplay = (phone) => {
    if (phone.length === 10) {
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    }
    return phone;
  };

  // Show loading while context is initializing
  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if org exists
  if (!org) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        px: 2 
      }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom>
            No loyalty program found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please check the URL and try again.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  // Check if org is inactive
  if (org && typeof org === 'object' && org.is_active === false) {
    setError('This loyalty program is not currently accepting new members');
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        px: 2 
      }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The loyalty program you're looking for may have been removed or is not available for public signup.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  if (submitSuccess) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          height: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          px: 2 
        }}>
          <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
            {getLogo() && (
              <Box
                component="img"
                src={getLogo()}
                alt={`${getName()} logo`}
                sx={{
                  height: 100,
                  objectFit: 'contain',
                  mx: 'auto',
                  mb: 3
                }}
              />
            )}
            <Typography variant="h5" color="primary" gutterBottom fontWeight="bold">
              Success!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              You've been enrolled in the {getName()} rewards program.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You'll receive a text message shortly with more information about your rewards.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate(`/register?phone=${phone}`)}
              sx={{ mt: 2 }}
            >
              Finish profile to redeem rewards
            </Button>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Container maxWidth="sm" sx={{ 
          py: 4, 
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1
        }}>
          {/* Header with logo and title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
              {getLogo() && (
                <Box
                  component="img"
                  src={getLogo()}
                  alt={`${getName()} logo`}
                  sx={{
                    height: 120,
                    objectFit: 'contain',
                    mx: 'auto',
                    mb: 3
                  }}
                />
              )}
              <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                Join our rewards program!
              </Typography>
              <Typography variant="h5" color="text.secondary">
                Would you like to receive special offers, promotions and updates from {getName()}?
              </Typography>
          </Box>

          {/* Form */}
          <Box sx={{ 
            backgroundColor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 1,
            flexGrow: 1
          }}>
            <form onSubmit={handleSubmit}>
              {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {submitError}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
                Enter your mobile number
              </Typography>

              <TextField
                fullWidth
                name="phone"
                label="Mobile Number"
                value={formatPhoneDisplay(phone)}
                onChange={handlePhoneChange}
                error={!!phoneError}
                helperText={phoneError}
                disabled={submitting}
                sx={{ mb: 3 }}
                inputProps={{
                  style: { fontSize: '1.2rem', textAlign: 'center' }
                }}
                InputLabelProps={{
                  style: { fontSize: '1.1rem' }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                  style: { fontSize: '1.2rem' }
                }}
              />

              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  By submitting your phone number, you agree to receive marketing communications via SMS to {phone ? formatPhoneDisplay(phone) : 'the number above'}. Message and data rates may apply. You can opt out at any time.
                </Typography>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{ mb: 3, py: 2, fontSize: '1.1rem' }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Join Rewards Program'}
              </Button>
            </form>
          </Box>
        </Container>

        {/* Footer */}
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              powered by
            </Typography>
            <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 1014 342.8" width="75" style={{ marginRight: '4px' }}>
              <defs>
                <style>
                  {`.cls-1 { fill: none; } .cls-2 { clip-path: url(#clippath); } .cls-3 { fill: #0994f2; }`}
                </style>
                <clipPath id="clippath">
                  <rect className="cls-1" x="0" y="0.2" width="1014" height="342.6"></rect>
                </clipPath>
              </defs>
              <g>
                <g id="Layer_1">
                  <g className="cls-2">
                    <g>
                      <path className="cls-3" d="M183.5,233.5c-12.6,17.1-59.2-8.2-64.6-24.8,26-12.3,17.5-34.3,5.3-35.3-16.5-1.5-40.3,25.9-43.7,68.5-2.9,36.7,20.4,54.3,50.5,52.8,29.6-1.9,61.2-16.1,82.1-58.8,2.9-6.9,23.3,7.4,20.9,11.3-39.3,75.9-92.7,93.9-146,92C36.4,337.3-1,297.2,0,246.3c1.5-64.1,54.4-119.9,132.1-118.9,70.4,1,89.3,56.5,51.5,106"></path>
                      <path className="cls-3" d="M203.4,274.8c5.8-24.8,12.2-49.5,19.2-74.2,6.3-22.4,6.8-70.1,34.3-74.5,6.3-1,65.6.5,61.5,14.7-16,56.2-31.1,100.2-35.9,126.8-2.9,18.7-.5,28.4,5.8,28.4,17,.5,46.1-23,62.6-58.8,2.9-6.9,23.3,7.4,20.9,11.8-22.3,42.1-59.7,89.1-115.1,91.5-51.9,2.4-62.6-25.9-53.4-65.6v-.2ZM260.7,60.5c4.9-23,23.3-41.1,49-40.6,25.2.5,38.4,18.5,34.5,40.6-3.9,22.4-21.8,40.6-50.5,40.6s-37.4-19-33-40.6Z"></path>
                      <path className="cls-3" d="M543.6,192.5c-4.9,20-15.5,49.3-20.4,71.9-3.9,18.2-1.5,31.3,7.3,31.3,18,0,42.2-27.4,57.3-58.8,3.4-6.9,23.3,6.9,20.9,11.8-19.4,37.2-57.3,90.4-112.2,92-50,1.5-61.2-26.4-51.5-65.1,3.9-15.2,16-65.1,18-73.8,2.9-13.2,2.9-21.6-10.7-19.5-10.2,1.5-21.8,8.7-34.5,19-14.1,45.9-22.3,86.2-33.5,131-8.2,11.8-72.3,14.2-78.2-2.9,12.9-57.2,29.4-113.6,46.4-169.7,2.9-9.8,4.4-25.4,12.9-31,5.8-3.8,16.7-3.6,23.3-3.8,4.4,0,49.3,4.3,45.4,14.6-2.9,7.9-4.9,16.6-7.8,24.5,17.5-13.7,48.6-37.2,77.7-36.7,37.9.5,48.6,26.4,39.3,65.1"></path>
                      <path className="cls-3" d="M747.6,234.5c-12.6,17.1-59.2-8.2-64.6-24.8,26-12.3,17.5-34.3,5.3-35.3-16.5-1.5-40.3,25.9-43.7,68.5-2.9,36.7,20.4,54.3,50.5,52.8,29.6-1.9,61.2-16.1,82.1-58.8,2.9-6.9,23.3,7.4,20.9,11.3-39.3,75.9-92.7,93.9-146,92-51.5-1.9-88.9-42.1-87.9-92.8,1.5-64.1,54.4-119.9,132.1-118.9,70.4,1,89.3,56.5,51.5,106"></path>
                      <path className="cls-3" d="M920.3,17.5c-9.2,27.9-26.7,84.1-41.8,142.3,18-13.7,47.6-32.2,74.8-32.2,42.7,0,52,26.9,40.3,65.1-9.7,31.9-40.8,93.7-15.3,102.9,8.5,3.1,15.3,3.6,28.2-7.7,4.1-3.8,9.4-.5,6.5,4.3-13.9,30.7-37.7,48.1-69.4,48.5-49.6.5-57.3-25.4-53.4-54.8,3.4-25,19.4-75.4,22.3-84.1,4.4-12.7,3.4-22.4-10.2-21.1-9.2,1-23.8,9.2-35.9,20.6-13.1,49.3-24.8,93.3-34,131.5-2.4,10.8-86.4,17.1-80.6-5,27.2-103.1,68-244.9,91.3-322.4,17-11.6,78.2-3.4,77.2,11.8"></path>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default GroupSignUp;